"use client";

import { useState } from "react";

import { BrandLockup } from "~/components/layout/brand-lockup";
import { MobileNavAccordion } from "~/components/storefront/nav/mobile-nav-accordion";
import { useNav } from "~/components/storefront/nav/nav-provider";
import { Dialog } from "~/components/ui/dialog";
import { useAccountIdentity } from "~/components/auth/signed-in-provider";
import { LocaleLink, useTranslator } from "~/i18n/provider";
import { MarketPill } from "~/components/storefront/market/market-pill";
import { useCartCount } from "~/lib/cart/use-cart-count";

/**
 * P2-01 · Mobile nav trigger + drawer shell (visible below the fixed 1024px
 * breakpoint). The drawer is a focus-trapped dialog (ui/dialog) holding a
 * labelled nav slot that P2-02 fills with the accordion category tree; until
 * then it carries the account entry so it is never an empty box.
 *
 * P17A-08 · `nav` comes from `useNav()` (one `<NavProvider>` crossing shared
 * with `PrimaryNav`), not a prop — see `nav-provider.tsx`.
 *
 * P17A-06 · The account entry comes from `useAccountIdentity()` rather than a
 * `userName` prop, for the same reason `AccountButton` does: the header that
 * renders this must not depend on `auth()`, or nothing under it can be
 * shared-cached. Same three states, and `account-button.tsx` has the argument.
 */
export function MobileMenuButton({
  wordmark,
  logoMarkUrl,
}: {
  wordmark: string;
  logoMarkUrl: string | null;
}) {
  const nav = useNav();
  const { signedIn, name: userName } = useAccountIdentity();
  const [open, setOpen] = useState(false);
  const cartCount = useCartCount();
  const t = useTranslator();

  return (
    <>
      <button
        type="button"
        aria-label={t("nav.openMenu")}
        aria-expanded={open}
        onClick={() => {
          setOpen(true);
        }}
        className="rounded-md p-2 text-fg hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] lg:hidden"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          className="h-5 w-5"
        >
          <path d="M4 6.5h16M4 12h16M4 17.5h16" />
        </svg>
      </button>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        placement="left"
        className="flex flex-col p-0"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <BrandLockup
            wordmark={wordmark}
            logoMarkUrl={logoMarkUrl}
            size="sm"
          />
          <button
            type="button"
            aria-label={t("nav.closeMenu")}
            onClick={() => setOpen(false)}
            className="rounded-md p-1.5 text-fg-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {/* Accordion category nav + custom links (P2-02). */}
        <nav
          aria-label={t("nav.menu")}
          className="flex-1 overflow-y-auto px-4 py-3"
        >
          <MobileNavAccordion items={nav} onNavigate={() => setOpen(false)} />
        </nav>

        {/* P11-19 · The market entry point on mobile, where the header pill is
            hidden. It closes the drawer as it opens the picker: the picker
            lives outside this dialog, so nesting one modal in another — and
            the ambiguous Escape that comes with it — never happens. */}
        <div className="border-t border-border px-4 py-2">
          <MarketPill variant="mobile" onOpen={() => setOpen(false)} />
        </div>

        {/* Account + cart within thumb reach (P2-DB03 §03). */}
        <div className="border-t border-border px-4 py-3">
          <LocaleLink
            href={signedIn ? "/account" : "/sign-in"}
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-fg hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {!signedIn
              ? t("common.signIn")
              : userName
                ? t("nav.accountLabel", { name: userName })
                : t("nav.account")}
          </LocaleLink>
          <button
            type="button"
            aria-label={t("nav.cartLabel", { count: cartCount })}
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-fg hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            <span>{t("nav.cart")}</span>
            {cartCount > 0 && (
              <span
                aria-hidden="true"
                className="ml-auto rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-fg"
              >
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </Dialog>
    </>
  );
}
